import { FC, useState } from 'react';
import { Radio, Spin, Tag } from 'antd';
import { CustomModal } from 'components/molecules/PricingSimulator/Modals/ScenarioColumnEditModal';
import { CustomSelect } from 'components/molecules/PricingSimulator/DimSection';
import DownSvg from 'assets/svg/Down';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';

type Props = {
    open: boolean;
    onSubmit: (payload: { kpiIds?: string[]; resetOption: 'filter_items' | 'all' }) => void;
    onCancel: () => void;
    loading?: boolean;
    kpiOptions?: { label: string; value: string }[];
    title: string;
};

const selectAllObject = { label: 'Select All', value: 'select_all' };
const SELECT_ALL_VALUE = selectAllObject.value;

const ResetPromoLibraryModal: FC<Props> = ({ open, onSubmit, onCancel, loading = false, kpiOptions = [], title }) => {
    const [selectedKpis, setSelectedKpis] = useState<string[]>([]);
    const [resetOption, setResetOption] = useState<'filter_items' | 'all'>('filter_items');

    const allKpiValues = kpiOptions.map((kpi) => kpi.value);
    const optionsWithSelectAll = kpiOptions?.length ? [selectAllObject, ...kpiOptions] : [];
    const updatedSelectedKpis = selectedKpis.filter((item) => item !== SELECT_ALL_VALUE);
    const isResetDisabled = updatedSelectedKpis.length === 0;

    const handleKpiChange = (value: unknown) => {
        const selectedValues = (value as string[]) || [];
        const hasSelectAll = selectedValues.includes(SELECT_ALL_VALUE);
        const hadSelectAll = selectedKpis.includes(SELECT_ALL_VALUE);

        if (hadSelectAll && !hasSelectAll) {
            setSelectedKpis([]);
            return;
        }

        if (!hadSelectAll && hasSelectAll) {
            setSelectedKpis([SELECT_ALL_VALUE, ...allKpiValues]);
            return;
        }

        setSelectedKpis(selectedValues);
    };

    const handleReset = () => {
        onSubmit({
            kpiIds: updatedSelectedKpis,
            resetOption,
        });
    };

    const handleCancel = () => {
        setSelectedKpis([]);
        setResetOption('filter_items');
        onCancel();
    };

    const tagRender = (props: any) => {
        if (props.value === SELECT_ALL_VALUE) {
            return <></>;
        }
        return <Tag style={{ marginRight: 4 }}>{props.label}</Tag>;
    };

    return (
        <CustomModal
            style={{ borderRadius: 8 }}
            cancelButtonProps={{ style: { borderRadius: 6 } }}
            okButtonProps={{
                disabled: isResetDisabled || loading,
                style: { borderRadius: 6 },
            }}
            onOk={handleReset}
            okText={loading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : 'Reset'}
            destroyOnClose
            centered
            open={open}
            width={420}
            onCancel={handleCancel}
            title={<p className="font-sm">{title}</p>}
        >
            <div className="rounded border border-backgroundGreyAlt6 p-4">
                {/* Select KPI Section */}
                <div className="mb-4">
                    <label className="block font-xs mb-2">Select KPI's to Reset</label>
                    <CustomSelect
                        mode="multiple"
                        showSearch={false}
                        placeholder="Select KPI's"
                        className="dropdown-height w-full"
                        suffixIcon={<DownSvg />}
                        loading={false}
                        value={selectedKpis}
                        disabled={false}
                        options={optionsWithSelectAll}
                        maxTagCount="responsive"
                        onChange={handleKpiChange}
                        tagRender={tagRender}
                    />
                </div>

                {/* Choose Reset Option Section */}
                <div>
                    <label className="block font-xs mb-2">Choose reset option</label>
                    <Radio.Group
                        value={resetOption}
                        onChange={(e) => setResetOption(e.target.value)}
                        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                    >
                        <Radio value="filter_items">Reset only filter items</Radio>
                        <Radio value="all">Reset All</Radio>
                    </Radio.Group>
                </div>
            </div>
        </CustomModal>
    );
};

export default ResetPromoLibraryModal;
